import React, { useState, useEffect } from "react";
import { Home, Lightbulb, Lock, Settings, Plus } from "lucide-react";
import { cn } from "@/shared/utils";
import { tuyaService, type TuyaDevice } from "@/domains/smarthome/services";

interface SmartHomeOutletProps {
  className?: string;
}

export const SmartHomeOutlet: React.FC<SmartHomeOutletProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [devices, setDevices] = useState<TuyaDevice[]>([]);

  // Auto-initialize Tuya and load devices on mount
  useEffect(() => {
    let didRun = false;
    const initializeAndLoad = async () => {
      if (didRun) return; // StrictMode guard
      didRun = true;
      try {
        await tuyaService.autoInitialize();
        await loadSmartHomeData();
      } catch {
        // NOTE: Failed to initialize Tuya - silently ignore to prevent UI disruption
      }
    };
    initializeAndLoad();
    return () => {
      didRun = true;
    };
  }, []);

  const loadSmartHomeData = async () => {
    try {
      const connected = await tuyaService.isConnected();

      if (connected) {
        const deviceList = await tuyaService.getDevices();
        setDevices(deviceList);
      }
    } catch {
      // NOTE: Failed to load smart home data - silently ignore to prevent UI disruption
    }
  };

  // Calculate smart home status
  const smartHomeStatus = {
    totalDevices: devices.length,
    onlineDevices: devices.filter((d) => d.status === "Online").length,
    lightsOn: devices.filter((d) => d.device_type === "Light" && d.state).length,
    securityStatus: "secure",
  };

  // Get quick devices (first 4 online devices)
  const quickDevices = devices
    .filter((device) => device.status === "Online")
    .slice(0, 4)
    .map((device) => ({
      id: device.id,
      name: device.name,
      type: device.device_type.toLowerCase(),
      status: device.state,
      value: device.value,
    }));

  const toggleDevice = async (deviceId: string) => {
    try {
      const device = devices.find((d) => d.id === deviceId);
      if (!device) return;

      await tuyaService.toggleDevice(deviceId, !device.state);

      // Update local state
      setDevices((prev) => prev.map((d) => (d.id === deviceId ? { ...d, state: !d.state } : d)));
    } catch {
      // NOTE: Failed to toggle device - silently ignore to prevent UI disruption
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case "light":
        return <Lightbulb className="w-4 h-4" />;
      case "lock":
        return <Lock className="w-4 h-4" />;
      case "sensor":
        return <Home className="w-4 h-4" />;
      default:
        return <Home className="w-4 h-4" />;
    }
  };

  const getDeviceColor = (type: string, status: boolean) => {
    if (!status) return "text-neutral-400";
    switch (type) {
      case "light":
        return "text-yellow-400";
      case "lock":
        return "text-red-400";
      case "sensor":
        return "text-green-400";
      default:
        return "text-blue-400";
    }
  };

  return (
    <div className={cn("relative", className)}>
      {/* Smart Home Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-800/80 transition-all duration-200"
        title="Smart Home"
      >
        <Home className="w-5 h-5 text-blue-500" />
        <div className="hidden md:block text-left">
          <p className="text-xs font-medium text-white">{smartHomeStatus.onlineDevices} devices</p>
          <p className="text-xs text-neutral-400">{smartHomeStatus.lightsOn} lights</p>
        </div>
      </button>

      {/* Smart Home Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-16 right-4 w-80 bg-neutral-800 rounded-xl border border-neutral-700/50 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-700/50">
              <div className="flex items-center gap-2">
                <Home className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-white">Smart Home</h3>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-1 text-neutral-400 hover:text-white transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Status Overview */}
            <div className="p-4 border-b border-neutral-700/50">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{smartHomeStatus.onlineDevices}</p>
                  <p className="text-xs text-neutral-400">Online</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{smartHomeStatus.lightsOn}</p>
                  <p className="text-xs text-neutral-400">Lights On</p>
                </div>

                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">🔒</p>
                  <p className="text-xs text-neutral-400">Secure</p>
                </div>
              </div>
            </div>

            {/* Quick Controls */}
            <div className="p-4">
              <h4 className="text-sm font-medium text-neutral-300 mb-3">Quick Controls</h4>
              <div className="space-y-2">
                {quickDevices.map((device) => (
                  <div
                    key={device.id}
                    className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg hover:bg-neutral-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`${getDeviceColor(device.type, device.status)}`}>
                        {getDeviceIcon(device.type)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{device.name}</p>
                      </div>
                    </div>
                    {device.type !== "sensor" && (
                      <button
                        onClick={() => toggleDevice(device.id)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          device.status
                            ? "bg-green-500/20 text-green-400"
                            : "bg-neutral-700 text-neutral-300"
                        }`}
                      >
                        {device.status ? "On" : "Off"}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mt-4 space-y-2">
                <button className="w-full flex items-center justify-center gap-2 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors">
                  <Settings className="w-4 h-4" />
                  <span className="text-sm">All Settings</span>
                </button>
                <button className="w-full flex items-center justify-center gap-2 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-neutral-300 hover:bg-neutral-700 transition-colors">
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Add Device</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
